/**
 * useLatestHistorialMap
 *
 * Given a flat array of RevisionElementoHistorial entries, returns a Map keyed
 * by `historialKey(elementoTipo, elementoId)` (i.e. `${elementoTipo}:${elementoId}`)
 * → { estado, observacion }.
 *
 * Only terna-field tipos are indexed:
 *   OBSERVACIONES | PLANMEJORA | FECHAIMPLEMENTACION
 *
 * When multiple entries share the same key the one with the LATEST fecha wins
 * (most-recent verdict is the truth).
 */

import { historialKey } from '@/utils/revisionHistorial';

export interface HistorialEntry {
  elementoId: string | null;
  elementoTipo: string;
  estado: string;
  observacion: string | null;
  fecha: string;
}

export interface HistorialFullEntry extends HistorialEntry {
  id: number;
  adminNombre?: string | null;
}

export function buildHistorialFieldMap(historial: HistorialFullEntry[]): Map<string, HistorialFullEntry[]> {
  const map = new Map<string, HistorialFullEntry[]>();
  for (const entry of historial) {
    if (!entry.elementoId) continue;
    const key = historialKey(entry.elementoTipo, entry.elementoId);
    const arr = map.get(key);
    if (arr) arr.push(entry);
    else map.set(key, [entry]);
  }
  map.forEach((arr) => arr.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()));
  return map;
}

export interface HistorialVerdict {
  estado: string;
  observacion: string | null;
}

const INDEXED_TIPOS = new Set(['OBSERVACIONES', 'PLANMEJORA', 'FECHAIMPLEMENTACION', 'REQUISITO']);

export type HistorialMap = Map<string, HistorialVerdict>;

/**
 * Pure function version — useful for testing without React hooks overhead.
 * The React hook wraps this in useMemo.
 */
export function buildLatestHistorialMap(historial: HistorialEntry[]): HistorialMap {
  const map = new Map<string, { verdict: HistorialVerdict; fecha: number }>();

  for (const entry of historial) {
    if (!entry.elementoId) continue;
    if (!INDEXED_TIPOS.has(entry.elementoTipo)) continue;

    const key = historialKey(entry.elementoTipo, entry.elementoId);
    const ts = new Date(entry.fecha).getTime();
    const existing = map.get(key);

    if (!existing || ts > existing.fecha) {
      map.set(key, {
        verdict: { estado: entry.estado, observacion: entry.observacion },
        fecha: ts,
      });
    }
  }

  const result: HistorialMap = new Map();
  map.forEach((v, k) => result.set(k, v.verdict));
  return result;
}

import { useMemo } from 'react';

export function useLatestHistorialMap(historial: HistorialEntry[] | undefined): HistorialMap {
  return useMemo(() => buildLatestHistorialMap(historial ?? []), [historial]);
}
